import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ApiError, apiDownload, apiRequest } from "../../api/client";
import type { DictionaryKind, DictionarySummary } from "../../api/types";
import { useAuth } from "../../context/AuthContext";

export const KIND_LABEL: Record<DictionaryKind, string> = {
  CENTRAL: "AFA central",
  INDUSTRY: "Industry",
  CONFERENCE: "Conference",
};

export function KindBadge({ kind }: { kind: DictionaryKind }) {
  const cls = kind === "CENTRAL" ? "ok" : kind === "INDUSTRY" ? "warn" : "";
  return (
    <span className={`status-pill ${cls}`} style={kind === "CONFERENCE" ? { background: "#e8eef9", color: "#26417a" } : undefined}>
      {KIND_LABEL[kind]}
    </span>
  );
}

export default function DictionariesPage() {
  const { admin } = useAuth();
  const navigate = useNavigate();
  const isSuper = admin?.role === "SUPER_ADMIN";
  const [dictionaries, setDictionaries] = useState<DictionarySummary[] | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", kind: "INDUSTRY" as DictionaryKind });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    setDictionaries(await apiRequest<DictionarySummary[]>("/dictionaries"));
  }
  useEffect(() => {
    void load();
  }, []);

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const created = await apiRequest<DictionarySummary>("/dictionaries", { method: "POST", body: form });
      setShowForm(false);
      setForm({ name: "", description: "", kind: "INDUSTRY" });
      navigate(`/operator/dictionaries/${created.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not create the dictionary");
    } finally {
      setBusy(false);
    }
  }

  async function remove(d: DictionarySummary) {
    if (!confirm(`Delete the dictionary "${d.name}" and its ${d.termCount} terms? Conferences using it will stop using it.`)) return;
    try {
      await apiRequest(`/dictionaries/${d.id}`, { method: "DELETE" });
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : "Could not delete the dictionary");
    }
  }

  return (
    <div>
      <div className="toolbar">
        <h1 style={{ margin: 0, flex: 1 }}>Dictionaries</h1>
        {isSuper && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            + New dictionary
          </button>
        )}
      </div>

      <p style={{ fontSize: 13, color: "var(--text-muted)" }}>
        Each conference translates with a stack of dictionaries, highest priority first: the <strong>AFA central</strong>{" "}
        dictionary (always loaded, always wins), then the dictionaries assigned to that conference in the order you set
        on its page. Anything no dictionary covers is left to the general translation model. Changes apply to running
        sessions on the next sentence.
      </p>

      {dictionaries === null && <p>Loading…</p>}
      {dictionaries && (
        <table className="table">
          <thead>
            <tr>
              <th>Dictionary</th>
              <th>Type</th>
              <th>Terms</th>
              <th>Conferences</th>
              <th>Times used</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {dictionaries.map((d) => (
              <tr key={d.id} style={d.isActive ? undefined : { opacity: 0.55 }}>
                <td>
                  <Link to={`/operator/dictionaries/${d.id}`} style={{ fontWeight: 700 }}>
                    {d.name}
                  </Link>
                  {!d.isActive && <span className="chip">off</span>}
                  {d.description && <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>{d.description}</div>}
                </td>
                <td>
                  <KindBadge kind={d.kind} />
                </td>
                <td>{d.termCount}</td>
                <td>{d.conferenceCount === null ? "All" : d.conferenceCount}</td>
                <td>{d.totalHits}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <Link className="btn-ghost" to={`/operator/dictionaries/${d.id}`}>
                    Open
                  </Link>{" "}
                  <button className="btn-ghost" onClick={() => void apiDownload(`/dictionaries/${d.id}/export.xlsx`, `${d.name}.xlsx`)}>
                    Excel
                  </button>{" "}
                  <button className="btn-ghost" onClick={() => void apiDownload(`/dictionaries/${d.id}/export.csv`, `${d.name}.csv`)}>
                    CSV
                  </button>
                  {isSuper && d.kind !== "CENTRAL" && (
                    <>
                      {" "}
                      <button className="btn-ghost" onClick={() => void remove(d)}>
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="modal-backdrop" onClick={() => setShowForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>New dictionary</h2>
            <div className="stack">
              <div className="field">
                <label>Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus placeholder="e.g. Fertilizer Industry" />
              </div>
              <div className="field">
                <label>Description (optional)</label>
                <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="field">
                <label>Type</label>
                <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as DictionaryKind })}>
                  <option value="INDUSTRY">Industry — a domain glossary you assign to conferences</option>
                  <option value="CONFERENCE">Conference — terms for one specific event</option>
                  <option value="CENTRAL">AFA central — loaded for EVERY conference, always highest priority</option>
                </select>
              </div>
              {error && <div className="error-banner">{error}</div>}
            </div>
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowForm(false)}>
                Cancel
              </button>
              <button className="btn-primary" disabled={busy || form.name.trim().length < 2} onClick={create}>
                {busy ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
